import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faLock, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import FalconCloseButton from 'components/common/FalconCloseButton';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const ModalKeepClosedTicket = ({ show, onClose, ticket, onTicketClosed, onCreateTicketNote }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, nombreCompleto } = useToken();
  const { showErrorToast, showSuccessToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const hasAssignedAgent = Boolean(ticket?.idUsuarioAsignado && Number(ticket.idUsuarioAsignado) > 0);
  const ticketId = ticket?.id || ticket?.idTicket;

  useEffect(() => {
    if (show) {
      setErrorMsg('');
      setIsSubmitting(false);
    }
  }, [show]);

  if (!show) return null;

  const handleConfirmKeepClosed = async () => {
    if (!hasAssignedAgent) {
      const msg = 'No se puede procesar el ticket porque no está asignado a ningún agente.';
      setErrorMsg(msg);
      showErrorToast(msg);
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      const response = await fetch('/api/SupportEngine/KeepClosedTicket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId)
        })
      });

      if (response.ok) {
        try {
          const noteText = `${nombreCompleto} volvió a cerrar el ticket.`;
          if (typeof onCreateTicketNote === 'function') {
            await onCreateTicketNote({
              note: noteText,
              noteColor: '#fee2e2'
            });
          } else {
            await fetch('/api/SupportEngine/CreateTicketNote', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: 'Bearer ' + token
              },
              body: JSON.stringify({
                ticketId: parseInt(ticketId),
                note: noteText,
                noteColor: '#fee2e2'
              })
            });
          }
        } catch (noteErr) {
          console.warn('Error al registrar nota de cierre:', noteErr);
        }

        showSuccessToast(`Ticket #${ticketId} mantenido cerrado correctamente`);
        if (onTicketClosed) {
          onTicketClosed();
        }
        onClose();
      } else {
        const err = await response.json().catch(() => ({}));
        const msg = err.message || 'Error al mantener cerrado el ticket';
        setErrorMsg(msg);
        showErrorToast(msg);
      }
    } catch (err) {
      const msg = 'Error de red: ' + err.message;
      setErrorMsg(msg);
      showErrorToast(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="md">
      <Modal.Header className="border-bottom-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2 text-warning fs-0">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>Mantener Cerrado Ticket #{ticketId}</span>
        </Modal.Title>
        <FalconCloseButton onClick={onClose} disabled={isSubmitting} />
      </Modal.Header>

      <Modal.Body className="pt-2">
        {!hasAssignedAgent && (
          <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 fs--1 mb-3" role="alert">
            <FontAwesomeIcon icon={faLock} className="flex-shrink-0 text-warning" />
            <div>
              Este ticket no está asignado a ningún agente. Debe asignarse a un agente antes de proceder.
            </div>
          </div>
        )}

        <p className="text-muted fs--1 mb-3">
          ¿Estás seguro de que deseas mantener cerrado el ticket para{' '}
          <strong className="text-dark">{ticket?.customerName || 'el cliente'}</strong>?
        </p>

        <div className="alert alert-info d-flex align-items-start gap-2 py-2 px-3 fs--1 mb-2" role="alert">
          <FontAwesomeIcon icon={faInfoCircle} className="flex-shrink-0 mt-1 text-info" />
          <div>
            El ticket pasará nuevamente a estatus <strong>Cerrado</strong>.
          </div>
        </div>

        {errorMsg && <div className="text-danger fs--1 mt-2">{errorMsg}</div>}
      </Modal.Body>

      <Modal.Footer
        className="px-4 py-2.5 border-top d-flex justify-content-end gap-2"
        style={{
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          borderColor: isDark ? '#233142' : '#e2e8f0'
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="btn btn-sm px-3 shadow-none fw-semibold"
          style={{
            fontSize: '0.82rem',
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            color: isDark ? '#94a3b8' : '#64748b',
            border: isDark ? '1px solid #334155' : '1px solid #cbd5e1'
          }}
        >
          No, cancelar
        </button>
        <Button
          variant="warning"
          size="sm"
          onClick={handleConfirmKeepClosed}
          disabled={isSubmitting || !hasAssignedAgent}
          className="d-flex align-items-center gap-1.5 px-3 py-1 fw-semibold text-dark"
          style={{ fontSize: '0.82rem' }}
        >
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
              <span>Guardando...</span>
            </>
          ) : (
            <>
              {!hasAssignedAgent && <FontAwesomeIcon icon={faLock} />}
              <span>Sí, Mantener Cerrado</span>
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ModalKeepClosedTicket.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ticket: PropTypes.object,
  onTicketClosed: PropTypes.func,
  onCreateTicketNote: PropTypes.func
};

export default ModalKeepClosedTicket;
